import { useCallback, useMemo, useState } from 'react';

export const SITE_CONSENT_STORAGE_KEY = 'leaguerunner-site-consent';

const enabledConsent = Object.freeze({
  analytics: true,
  advertising: true,
  status: 'accepted',
  updatedAt: 'system-default',
});

const sanitizeConsent = () => enabledConsent;

export const getStoredSiteConsent = () => {
  if (typeof window === 'undefined') {
    return enabledConsent;
  }

  try {
    window.localStorage.setItem(SITE_CONSENT_STORAGE_KEY, JSON.stringify(enabledConsent));
    return enabledConsent;
  } catch {
    return enabledConsent;
  }
};

const persistConsent = (nextConsent) => {
  if (typeof window === 'undefined') {
    return nextConsent;
  }

  window.localStorage.setItem(SITE_CONSENT_STORAGE_KEY, JSON.stringify(nextConsent));
  return nextConsent;
};

export const useSiteConsent = () => {
  const [consent, setConsent] = useState(() => getStoredSiteConsent());

  const updateConsent = useCallback((nextPartialConsent) => {
    setConsent((currentConsent) => {
      const nextConsent = sanitizeConsent({
        ...currentConsent,
        ...nextPartialConsent,
        updatedAt: new Date().toISOString(),
      });

      return persistConsent(nextConsent);
    });
  }, []);

  const acceptAll = useCallback(() => {
    updateConsent({ analytics: true, advertising: true, status: 'accepted' });
  }, [updateConsent]);

  const declineOptional = useCallback(() => {
    updateConsent({ analytics: false, advertising: false, status: 'declined' });
  }, [updateConsent]);

  const value = useMemo(
    () => ({
      consent,
      hasDecided: true,
      acceptAll,
      declineOptional,
      updateConsent,
    }),
    [acceptAll, consent, declineOptional, updateConsent],
  );

  return value;
};

export default useSiteConsent;